/**
 * CAPA — ação corretiva, verificação de eficácia e follow-up automático.
 *
 * O legado tem aqui o seu melhor ativo. O fluxo
 * Ishikawa → causa-raiz → tarefa 5W2H → verificação de eficácia → follow-up
 * é maduro e acima da média do mercado (plano §2.3). A reescrita PRESERVA o
 * fluxo e corrige a execução.
 *
 * Referência: docs/legacy-spec/sgq.md seções B.8, C.1, C.4, C.5, C.6.
 */
import { CapaActionStatus } from '@qualicloud/contracts';
import { IllegalTransitionError, StateMachine } from './state-machine.js';
/**
 * Reexportado, NÃO redefinido. O valor persistido é contrato
 * (as entidades do CORE + `@qualicloud/contracts`), e o domínio consome. Manter duas
 * listas seria repetir o BUG-NC-07 do legado, que tinha o catálogo de tipos de
 * NC em três cópias com textos divergentes.
 */
export { CapaActionStatus };
export class IncompleteActionPlanError extends Error {
    constructor(missingFields) {
        super(`O plano de ação 5W2H está incompleto. Faltam: ${missingFields.join(', ')}. ` +
            'ISO 13485 §8.5.2 exige que a ação corretiva seja definida antes de ser executada.');
        this.missingFields = missingFields;
        this.name = 'IncompleteActionPlanError';
    }
}
const FIVE_W2H_LABELS = {
    what: 'O quê',
    why: 'Por quê',
    where: 'Onde',
    when: 'Quando',
    who: 'Quem',
    how: 'Como',
};
export function assertActionPlanIsComplete(plan) {
    const missing = Object.keys(FIVE_W2H_LABELS).filter((field) => {
        const value = plan[field];
        if (value instanceof Date)
            return Number.isNaN(value.getTime());
        return value === undefined || value === null || String(value).trim() === '';
    });
    if (missing.length > 0) {
        throw new IncompleteActionPlanError(missing.map((f) => FIVE_W2H_LABELS[f] ?? f));
    }
}
/**
 * A origem da solicitação combina com o que o TIPO dela exige?
 *
 * Três combinações são possíveis e só uma é inválida:
 *
 *   · tipo exige causa + tem causa   → ação corretiva clássica, nascida do
 *     tratamento de uma não-conformidade;
 *   · tipo não exige + não tem causa → solicitação AVULSA. Sempre existiu no
 *     legado (`erp_solicitacao.id_ishikawa_causa` NULL, sgq.md §775) e a Onda 2
 *     fechou por engano ao tornar a coluna obrigatória;
 *   · tipo não exige + TEM causa     → também válido: nada impede registrar a
 *     causa de uma melhoria;
 *   · tipo EXIGE + não tem           → é o caso que este guarda recusa. Chamar
 *     de "ação corretiva" o que não sabe qual causa ataca é tratar sintoma com
 *     nome de tratamento — ISO 13485 §8.5.2 pede o contrário.
 */
export function assertRequestOriginIsCoherent(params) {
    if (params.requiresRootCause && !params.hasRootCause) {
        throw new NoRootCauseForTypeError(params.typeLabel);
    }
}
export class NoRootCauseForTypeError extends Error {
    constructor(typeLabel) {
        super(`O tipo "${typeLabel}" exige que a solicitação parta de uma causa-raiz. ` +
            'Escolha um tipo que dispense a análise de causa, ou abra a solicitação ' +
            'a partir do Ishikawa da não-conformidade.');
        this.name = 'NoRootCauseForTypeError';
    }
}
/**
 * O 5W2H é cobrado por inteiro?
 *
 * "Por quê" e "como" só são obrigatórios quando o tipo pede o plano completo.
 * Um pedido de troca de lâmpada preenchido com "por quê: porque queimou" e
 * "como: trocando" não é prestação de contas — é campo obrigatório derrotado, e
 * o efeito colateral é ensinar o usuário a preencher formulário sem pensar,
 * inclusive quando o formulário importa.
 */
export function assertPlanDepthMatchesType(params) {
    if (!params.requiresFullPlan)
        return;
    const faltando = [];
    if (params.why.trim() === '')
        faltando.push('Por quê');
    if (params.how.trim() === '')
        faltando.push('Como');
    if (faltando.length > 0)
        throw new IncompleteActionPlanError(faltando);
}
// ─────────────────────────────────────────────────────────────────────────────
// Ciclo de vida da ação corretiva
// ─────────────────────────────────────────────────────────────────────────────
/**
 * No legado o estado da tarefa não era uma coluna: era DERIVADO de
 * `dt_inicio`/`dt_fim`/`dt_cancela`/`analise_aprovada` por um `CASE` em SQL
 * (sgq.md §H). Isso produziu estados impossíveis — o mais grave sendo
 * `dt_fim = NULL` junto com `analise_aprovada = 0` após uma reprovação, que o
 * `CASE` lia como "em andamento" enquanto `finalizarNc` a contava como
 * "reprovada" apenas se `dt_fim` não fosse vazio (BUG-TAR-02).
 *
 * Aqui o estado é explícito e vive em `@qualicloud/contracts` (reexportado
 * acima), porque é valor persistido.
 */
export const CapaActionEvent = {
    START: 'START',
    COMPLETE: 'COMPLETE',
    VERIFY_EFFECTIVE: 'VERIFY_EFFECTIVE',
    VERIFY_INEFFECTIVE: 'VERIFY_INEFFECTIVE',
    REOPEN: 'REOPEN',
    CANCEL: 'CANCEL',
};
export const capaActionMachine = new StateMachine('CapaAction', [
    {
        from: CapaActionStatus.PLANNED,
        to: CapaActionStatus.IN_PROGRESS,
        event: CapaActionEvent.START,
        description: 'Responsável inicia a execução. Só o responsável pode iniciar.',
    },
    {
        from: CapaActionStatus.PLANNED,
        to: CapaActionStatus.AWAITING_VERIFICATION,
        event: CapaActionEvent.COMPLETE,
        description: 'Conclusão sem passar por "em andamento" — o legado permitia, porque o estado ' +
            'era derivado das datas e `dt_fim` podia ser gravado sem `dt_inicio`.',
    },
    {
        from: CapaActionStatus.IN_PROGRESS,
        to: CapaActionStatus.AWAITING_VERIFICATION,
        event: CapaActionEvent.COMPLETE,
        description: 'Responsável registra a conclusão e submete à verificação de eficácia.',
    },
    {
        from: CapaActionStatus.AWAITING_VERIFICATION,
        to: CapaActionStatus.EFFECTIVE,
        event: CapaActionEvent.VERIFY_EFFECTIVE,
        requiresSignatureAct: 'EFFECTIVENESS_VERIFY',
        description: 'Verificador aprova a eficácia. ISO 13485 §8.5.2. No legado era um UPDATE de ' +
            'flag sem assinatura nenhuma.',
    },
    {
        from: CapaActionStatus.AWAITING_VERIFICATION,
        to: CapaActionStatus.INEFFECTIVE,
        event: CapaActionEvent.VERIFY_INEFFECTIVE,
        requiresSignatureAct: 'EFFECTIVENESS_REJECT',
        description: 'Verificador reprova. Justificativa obrigatória, ação reaberta e SACAP de ' +
            'follow-up gerada — sgq.md §B.8.',
    },
    {
        from: CapaActionStatus.INEFFECTIVE,
        to: CapaActionStatus.IN_PROGRESS,
        event: CapaActionEvent.REOPEN,
        description: 'A reprovação reabre a ação para novo tratamento. No legado isso era feito ' +
            'zerando `dt_fim`, o que deixava a ação num estado que o próprio sistema ' +
            'lia de duas formas diferentes (BUG-TAR-02).',
    },
    {
        from: CapaActionStatus.PLANNED,
        to: CapaActionStatus.CANCELLED,
        event: CapaActionEvent.CANCEL,
        description: 'Cancelamento antes do início.',
    },
    {
        from: CapaActionStatus.IN_PROGRESS,
        to: CapaActionStatus.CANCELLED,
        event: CapaActionEvent.CANCEL,
        description: 'Cancelamento durante a execução, com justificativa.',
    },
]);
// ─────────────────────────────────────────────────────────────────────────────
// Quem verifica a eficácia
// ─────────────────────────────────────────────────────────────────────────────
export class NotTheVerifierError extends Error {
    constructor() {
        super('Esta ação não está aguardando a sua verificação de eficácia. ' +
            'Apenas quem solicitou a ação pode verificar se ela resolveu o problema.');
        this.name = 'NotTheVerifierError';
    }
}
export class EffectivenessReasonRequiredError extends Error {
    constructor() {
        super('Informe a justificativa quando a ação não resolveu o problema.');
        this.name = 'EffectivenessReasonRequiredError';
    }
}
export class SelfVerificationError extends Error {
    constructor() {
        super('O responsável pela execução não pode verificar a eficácia da própria ação. ' +
            'Segregação de funções — ISO 13485 §8.5.2 exige verificação independente de que ' +
            'a ação corretiva foi eficaz.');
        this.name = 'SelfVerificationError';
    }
}
/**
 * Porteiro da verificação de eficácia.
 *
 * REGRA PRESERVADA (sgq.md §C.5): o verificador é o **SOLICITANTE** da ação
 * (`id_solicitante`), não o gestor da qualidade nem o emissor da NC. Estava
 * hard-coded no `WHERE` do model (`model/reuniao/tarefas.php:282`) e é uma
 * escolha defensável: quem pediu a ação é quem sabe se o problema dele acabou.
 *
 * DIVERGÊNCIA D-CAPA-01: acrescentamos a segregação de funções. No legado, se o
 * solicitante fosse também o responsável pela execução — o que acontece —, ele
 * verificava a própria ação. `allowSelfVerification` existe pelo mesmo motivo
 * que `allowSelfApproval` no GED: organização pequena tem uma pessoa só. Default
 * `false`, e a escolha fica registrada na trilha.
 */
export function assertCanVerifyEffectiveness(params) {
    if (params.status !== CapaActionStatus.AWAITING_VERIFICATION) {
        throw new IllegalTransitionError('CapaAction', params.status, 'verificação de eficácia');
    }
    if (params.actorUserId !== params.requesterUserId) {
        throw new NotTheVerifierError();
    }
    if (!params.allowSelfVerification && params.actorUserId === params.assigneeUserId) {
        throw new SelfVerificationError();
    }
    // sgq.md §C.5: justificativa obrigatória quando reprovada.
    if (!params.approved && (params.reason ?? '').trim() === '') {
        throw new EffectivenessReasonRequiredError();
    }
}
// ─────────────────────────────────────────────────────────────────────────────
// Follow-up automático — a regra dos +3 dias
// ─────────────────────────────────────────────────────────────────────────────
/** Prefixo literal que o relatório da reunião procura. `reuniao.php:167` */
export const REOPEN_EXECUTION_PREFIX = '[Justificativa - Reabertura]';
export const FOLLOW_UP_OFFSET_DAYS = 3;
export const FOLLOW_UP_START_HOUR = 9;
export const FOLLOW_UP_END_HOUR = 10;
/**
 * Janela da SACAP de follow-up.
 *
 * REGRA PRESERVADA (sgq.md §B.8): `strtotime('+3 days 09:00')` no PHP significa
 * **hoje + 3 dias, às 09:00 em ponto** — o horário absoluto sobrepõe a hora
 * corrente. A reunião fica sempre em D+3 09:00 → 10:00, duração fixa de 1 hora.
 * Não é "daqui a 72 horas".
 *
 * DIVERGÊNCIA D-CAPA-02: o legado usava o fuso do SERVIDOR. Aqui a janela é
 * calculada no fuso da ORGANIZAÇÃO — uma reunião marcada para "09:00" precisa
 * ser 09:00 para quem vai comparecer, não para o datacenter.
 *
 * DIVERGÊNCIA D-CAPA-03: o legado não ajustava fim de semana. Marcar a
 * reavaliação de uma ação corretiva reprovada para um domingo garante que
 * ninguém apareça, e o registro fica com uma reunião fantasma. `skipWeekend`
 * empurra para a segunda-feira seguinte; default `true`.
 *
 * O cálculo é feito sobre os componentes de data no fuso alvo, e não por
 * aritmética de milissegundos, porque somar 72 h atravessa mudanças de horário
 * de verão e produz 08:00 ou 10:00 em vez de 09:00.
 */
export function computeFollowUpWindow(params) {
    const { now, timeZone } = params;
    const skipWeekend = params.skipWeekend ?? true;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    // Data civil no fuso da organização, deslocada em dias de calendário.
    const target = new Date(Date.UTC(get('year'), get('month') - 1, get('day') + FOLLOW_UP_OFFSET_DAYS));
    if (skipWeekend) {
        const dow = target.getUTCDay(); // 0 = domingo, 6 = sábado
        if (dow === 6)
            target.setUTCDate(target.getUTCDate() + 2);
        else if (dow === 0)
            target.setUTCDate(target.getUTCDate() + 1);
    }
    const year = target.getUTCFullYear();
    const month = target.getUTCMonth();
    const day = target.getUTCDate();
    return {
        startsAt: zonedTimeToUtc(year, month, day, FOLLOW_UP_START_HOUR, timeZone),
        endsAt: zonedTimeToUtc(year, month, day, FOLLOW_UP_END_HOUR, timeZone),
    };
}
/**
 * Converte "tal hora civil, neste fuso" para o instante UTC correspondente.
 *
 * Sem biblioteca de fuso: estima o deslocamento consultando o próprio
 * `Intl.DateTimeFormat` e corrige. Duas iterações bastam porque o offset só muda
 * nas transições de horário de verão, e a segunda passada já cai do lado certo.
 */
function zonedTimeToUtc(year, month, day, hour, timeZone) {
    let utc = Date.UTC(year, month, day, hour, 0, 0, 0);
    for (let i = 0; i < 2; i += 1) {
        const offsetMs = zoneOffsetMs(new Date(utc), timeZone);
        const corrected = Date.UTC(year, month, day, hour, 0, 0, 0) - offsetMs;
        if (corrected === utc)
            break;
        utc = corrected;
    }
    return new Date(utc);
}
/** Deslocamento do fuso, em ms, no instante dado. */
function zoneOffsetMs(at, timeZone) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = dtf.formatToParts(at);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return asUtc - at.getTime();
}
/** Assunto da SACAP de follow-up. Texto preservado de `tarefas.php:337`. */
export function followUpSubject(actionTitle) {
    const truncated = actionTitle.slice(0, 120);
    return `SACAP (Follow-up) — Reavaliação de ação reprovada: ${truncated}`;
}
/** Pauta da SACAP de follow-up. Texto preservado de `tarefas.php:338-341`. */
export function followUpAgenda(params) {
    return ('Esta SACAP foi gerada automaticamente após a reprovação da análise de eficácia ' +
        `da ação ${params.actionReference}.\n\n` +
        `Motivo da reprovação: ${params.reason}\n\n` +
        'Objetivo: revisar a causa-raiz e redefinir o plano de ação.');
}
/**
 * Entrada do log append-only da SACAP original. Preservado de `tarefas.php:392-395`,
 * inclusive o formato de data brasileiro, porque este texto é lido por humanos
 * na ata e a mudança de formato quebraria a leitura de registros antigos.
 */
export function followUpLogEntry(params) {
    const stamp = new Intl.DateTimeFormat('pt-BR', {
        timeZone: params.timeZone,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(params.at);
    return (`[${stamp}] Ação ${params.actionReference} reprovada. ` +
        `Nova SACAP ${params.newMeetingReference} criada para reavaliar. ` +
        `Motivo: ${params.reason}`);
}
export function buildFollowUpPlan(params) {
    return {
        window: computeFollowUpWindow({
            now: params.now,
            timeZone: params.timeZone,
            skipWeekend: params.skipWeekend,
        }),
        subject: followUpSubject(params.actionTitle),
        agenda: followUpAgenda({
            actionReference: params.actionReference,
            reason: params.reason,
        }),
        justification: 'Follow-up automático por eficácia reprovada',
        inheritedNonconformityIds: params.inheritedNonconformityIds,
        inheritedParticipantUserIds: params.inheritedParticipantUserIds,
        movesActionToNewMeeting: true,
        originalMeetingLogEntry: followUpLogEntry({
            at: params.now,
            timeZone: params.timeZone,
            actionReference: params.actionReference,
            newMeetingReference: params.newMeetingReference,
            reason: params.reason,
        }),
        reopenExecutionText: `${REOPEN_EXECUTION_PREFIX} ${params.reason}`,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Prazos
// ─────────────────────────────────────────────────────────────────────────────
export const CapaDueState = {
    ON_TIME: 'ON_TIME',
    DUE_SOON: 'DUE_SOON',
    OVERDUE: 'OVERDUE',
    DONE: 'DONE',
};
/**
 * Situação de prazo da ação.
 *
 * No legado os baldes do painel de tarefas **se sobrepunham** (BUG do §E.1): a
 * mesma ação era contada em mais de um, e a soma não fechava com o total. Aqui a
 * classificação é total e mutuamente exclusiva por construção.
 */
export function resolveCapaDueState(params) {
    const terminal = [
        CapaActionStatus.EFFECTIVE,
        CapaActionStatus.CANCELLED,
    ];
    if (terminal.includes(params.status))
        return CapaDueState.DONE;
    const MS_PER_DAY = 86_400_000;
    const due = Date.UTC(params.dueDate.getUTCFullYear(), params.dueDate.getUTCMonth(), params.dueDate.getUTCDate());
    const today = Date.UTC(params.now.getUTCFullYear(), params.now.getUTCMonth(), params.now.getUTCDate());
    const days = Math.round((due - today) / MS_PER_DAY);
    if (days < 0)
        return CapaDueState.OVERDUE;
    if (days <= (params.warningDays ?? 7))
        return CapaDueState.DUE_SOON;
    return CapaDueState.ON_TIME;
}
//# sourceMappingURL=capa.js.map